export interface IntegrationFormData {
  auth_fields: Record<string, string>;
}

export type IntegrationStatus = 'active' | 'inactive' | 'pending' | 'failed' | 'connected' | 'disconnected' | 'syncing' | 'synced';

export type IntegrationClassification = 
  | 'llm'
  | 'project_management' 
  | 'communication'
  | 'storage'
  | 'crm'
  | 'analytics'
  | 'other';

// Campo de autenticação dinâmico
export interface AuthField {
  key: string;
  type: 'text' | 'password' | 'email' | 'number' | 'select';
  label: string;
  secret?: boolean;
  required?: boolean;
  options?: string[]; // Para campos select
  description?: string;
}

// Campo de configuração adicional
export interface FormField {
  key?: string;
  name: string;
  type: 'text' | 'password' | 'email' | 'number' | 'select' | 'checkbox';
  label: string;
  required?: boolean;
  description?: string;
  placeholder?: string;
  options?: string[]; // Para campos select
  default?: string | number | boolean;
  readonly?: boolean; // Se true, o campo é exibido mas não editável (ex: webhook_url computado)
}

// Estrutura do auth_fields
export interface AuthFieldsStructure {
  fields: AuthField[];
  headers: Record<string, string>;
  auth_type: 'bearer' | 'basic' | 'api_key' | 'oauth2';
}

// Estrutura do settings para TEMPLATE (com form_fields)
export interface SettingsStructure {
  description?: string | { en: string; pt: string }; // Descrição traduzível
  block?: boolean; // Se true, bloqueia edição/exclusão da integration_base
  form_fields: FormField[];
}

/**
 * Estado de uma tool na instância (`mcp_config.tool_states`, AB#87542). Ausente = `enabled`.
 * `confirm` pede confirmação antes de executar; `blocked` some da lista dos agentes.
 */
export type ToolState = 'enabled' | 'confirm' | 'blocked';

/**
 * Tipo da integração (AB#87477): `api` é o conector REST; `mcp` é um servidor MCP remoto, do
 * qual a api vira cliente. No template vem em `kind`; na instância, em `mcp_config.kind` —
 * ausente nos dois casos quer dizer `api`.
 */
export type IntegrationKind = 'api' | 'mcp';

/** Como o servidor MCP autentica. */
export type McpAuthMode = 'none' | 'bearer' | 'apikey' | 'oauth';

/** Revisão do catálogo: template criado por uma organização nasce `pending`. */
export type CatalogReviewStatus = 'published' | 'pending';

/** Dicas que o servidor MCP declara sobre a tool — só informativas. */
export interface IntegrationToolAnnotations {
  title?: string;
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
  [key: string]: unknown;
}

export type IntegrationToolMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Uma operação do conector de API. Os campos além dos listados (`intents`, `body_template`,
 * `query_params`...) passam adiante sem a tela mexer.
 */
export interface IntegrationTool {
  name: string;
  title?: string;
  description?: string;
  inputSchema?: Record<string, unknown>;
  /** Só em tool de servidor MCP (AB#87477). */
  annotations?: IntegrationToolAnnotations;
  method?: IntegrationToolMethod;
  endpoint?: string;
  [key: string]: unknown;
}

/** `mcp_config` da instância: cópia do template na instalação + `tool_states`. */
export interface IntegrationToolsConfig {
  tools?: IntegrationTool[];
  tool_states?: Record<string, ToolState>;
  [key: string]: unknown;
}

/** `mcp_config` com os campos do servidor MCP remoto (AB#87477). */
export interface McpConfig extends IntegrationToolsConfig {
  kind?: IntegrationKind;
  enabled?: boolean;
  server_url?: string;
  transport?: 'streamable_http' | 'sse';
  auth_mode?: McpAuthMode;
  /** Nome do header, só no modo `apikey`. */
  auth_header?: string;
  tools_hash?: string;
  tools_synced_at?: string;
}

/** `settings.mcp_oauth` da instância de um servidor MCP com OAuth (AB#87477). */
export interface McpOAuthSettings {
  status: 'pending' | 'connected' | 'reconnect';
  /** Servidor de autorização descoberto pela api (RFC 9728/8414). */
  authorization_server_url?: string;
  client_id?: string;
  resource?: string;
  scope?: string;
  expires_at?: string;
}

// Estrutura do settings para INSTÂNCIA (valores simples)
export interface SettingsInstance {
  [key: string]: unknown; // Campos dinâmicos com valores simples
}

// Template de integração (endpoint /integrations)
export interface Integration {
  id: string;
  name: string; // Nome técnico (ex: "gemini")
  label: string; // Nome amigável (ex: "Google Gemini")
  description: string | null;
  classification: IntegrationClassification;
  auth_type?: string; // Tipo de autenticação
  auth_fields: AuthFieldsStructure; // Formulário dinâmico de autenticação
  settings: SettingsStructure | null; // Configurações adicionais
  /**
   * Descrição técnica do template: `models` (por modelo: rota, api-version
   * e, quando divergir, provider e endpoint) e `cost_source`. Não é editável na tela —
   * a tela apenas SUGERE os modelos declarados aqui (AB#85434).
   */
  metadata?: Record<string, unknown> | null;
  logo_url: string | null;
  url: string | null;
  status: IntegrationStatus;
  active: boolean; // Indica se o template está ativo/disponível
  created_at: string;
  updated_at: string;
  organization_id: string;
  /** Campos derivados pelo DTO (AB#87477) — ausentes em backends antigos; `kind` ausente = `api`. */
  kind?: IntegrationKind;
  provider?: string | null;
  server_url?: string | null;
  auth_mode?: McpAuthMode | null;
  review_status?: CatalogReviewStatus;
  tool_count?: number;
  mcp_config?: McpConfig | null;
}

// Instância de integração configurada (endpoint /integrations-base)
export interface IntegrationBase {
  id: string;
  name: string;
  label: string;
  title?: string; // Nome customizado pelo usuário (ex: "Jira — Mobile")
  description: string | null;
  classification: IntegrationClassification;
  organization_id: string;
  integration_id: string;
  auth_type?: string; // Tipo de autenticação copiado do template
  auth_fields: Record<string, unknown>;
  settings?: SettingsInstance | null; // Valores simples dos campos (não a estrutura do template)
  /** Copiado do template na instalação — ver `Integration.metadata` (AB#85434). */
  metadata?: Record<string, unknown> | null;
  logo_url: string | null; // Logo copiada do template
  url: string | null; // URL copiada do template
  status: IntegrationStatus;
  is_encrypted: boolean;
  last_validated_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  template?: Integration; // Incluído quando with_template=true
  scope?: 'local' | 'global'; // Persistido no banco
  /** Cópia do template + `tool_states` (AB#87542). */
  mcp_config?: McpConfig | null;
}

/** Template existente com a mesma `server_url`, devolvido pelo inspect. */
export interface McpExistingTemplate {
  id: string;
  label: string | null;
  logo_url: string | null;
}

/** `POST /integration-bases/mcp/inspect`. */
export interface McpInspectRequest {
  server_url: string;
  auth_mode?: McpAuthMode;
  credentials?: Record<string, string>;
  auth_header?: string;
}

export interface McpInspectResult {
  server_url: string;
  existing_template: McpExistingTemplate | null;
  auth: { mode: McpAuthMode; required: boolean };
  server_info?: { name?: string; title?: string; version?: string };
  tools?: IntegrationTool[];
  transport?: 'streamable_http' | 'sse';
}

/** `POST /integration-bases/mcp`. */
export interface AddMcpServerRequest {
  server_url: string;
  title: string;
  label?: string;
  description?: string;
  scope: 'local' | 'global';
  product_id?: string;
  auth_mode: McpAuthMode;
  credentials?: Record<string, string>;
  auth_header?: string;
}

/** Mesmo envelope do `POST /integration-bases`, mais `oauth_required`. */
export interface AddMcpServerResult {
  integration: IntegrationBase;
  post_install?: IntegrationPostInstall;
  oauth_required: boolean;
}

/** Diff de tools entre a instância e o template atual (`GET .../template-diff`). */
export interface ToolsDiff {
  added: IntegrationTool[];
  removed: IntegrationTool[];
  changed: { name?: string; before: IntegrationTool; after: IntegrationTool }[];
}

/** `GET .../mcp/refresh`: o diff do servidor e as skills que usam tools que saíram (AB#87477). */
export interface McpRefreshPreview extends ToolsDiff {
  affected_skills: { name: string; tools: string[] }[];
  /** Hash da lista mostrada; o apply só grava se o servidor ainda oferece essa lista. */
  tools_hash?: string;
}

/** `PUT /integration-bases/:id/tools`. */
export interface UpdateIntegrationToolsData {
  tools?: IntegrationTool[];
  tool_states?: Record<string, ToolState>;
}

export interface IntegrationUsageItem {
  toolName: string;
  timestamp: string;
  status: string;
  errorMessage?: string | null;
}

/** `GET /integration-bases/:id/usage`. */
export interface IntegrationUsage {
  items: IntegrationUsageItem[];
  counts: Record<string, number>;
}

export interface IntegrationPostInstallEndpoint {
  key: string;
  label: { pt?: string; en?: string; [k: string]: string | undefined };
  description?: { pt?: string; en?: string; [k: string]: string | undefined };
  value: string;
}

export interface IntegrationPostInstallManifestAsset {
  key: string;
  filename: string;
  url: string;
  label?: { pt?: string; en?: string; [k: string]: string | undefined };
}

// Informações opcionais retornadas pelo backend após criar uma integration_base.
// O template sinaliza (via settings.post_install) que deseja que o frontend exiba
// um modal com endpoints, manifest e instruções de setup. Pode incluir:
//   - endpoints[]      → múltiplos URLs nomeados a configurar no sistema externo
//   - manifest         → JSON renderizado (ex: Teams App manifest)
//   - manifest_assets  → arquivos binários a serem zipados junto ao manifest
//   - webhook_url      → campo legado (pré-endpoints[]), mantido para retrocompatibilidade
export interface IntegrationPostInstall {
  webhook_url?: string;
  setup_instructions?: {
    pt?: string[];
    en?: string[];
    [key: string]: string[] | undefined;
  };
  endpoints?: IntegrationPostInstallEndpoint[];
  manifest?: Record<string, unknown>;
  manifest_assets?: IntegrationPostInstallManifestAsset[];
  /** When true, render the manifest JSON in a code block (default false). */
  show_manifest_preview?: boolean;
  /** Per-template UI chrome labels (i18n maps). Falls back to built-in i18n keys. */
  labels?: {
    title?: { pt?: string; en?: string; [k: string]: string | undefined };
    subtitle?: { pt?: string; en?: string; [k: string]: string | undefined };
    manifest?: { pt?: string; en?: string; [k: string]: string | undefined };
    instructions?: { pt?: string; en?: string; [k: string]: string | undefined };
    download_zip?: { pt?: string; en?: string; [k: string]: string | undefined };
    copy_manifest?: { pt?: string; en?: string; [k: string]: string | undefined };
    copy?: { pt?: string; en?: string; [k: string]: string | undefined };
    confirm?: { pt?: string; en?: string; [k: string]: string | undefined };
  };
}

export interface CreateIntegrationBaseResult {
  integration: IntegrationBase;
  post_install?: IntegrationPostInstall;
}

// Dados para criação de nova integração base
/**
 * Só o que `POST /integration-bases` realmente recebe. Ficaram de fora
 * `organization_id` e `created_by` (derivados do token) e `auth_type`/`auth_fields`
 * (derivados do template no servidor).
 */
/**
 * Campo editável de `metadata`, declarado pelo template (AB#85605).
 *
 * Mesmo espírito de `settings.form_fields`: o cadastro diz o que a tela deve oferecer. Hoje o
 * tipo em uso é `models` — a lista de modelos —, e é assim que o Azure OpenAI pede que cada
 * deployment seja declarado por quem instala, já que o nome dele é escolhido no recurso do
 * cliente e nenhum template pode adivinhá-lo.
 */
export interface MetadataFieldDefinition {
  key: string;
  type: 'models' | 'text';
  required?: boolean;
  label?: string | { pt?: string; en?: string; [k: string]: string | undefined };
  description?: string | { pt?: string; en?: string; [k: string]: string | undefined };
}

export interface CreateIntegrationBaseData {
  /** Nome do provedor, vindo do template. */
  label: string;
  /** Nome desta instalação, dado por quem a cadastra (AB#85605). */
  title?: string;
  /** Campos de `metadata` declarados pelo template — hoje, o catálogo de modelos. */
  metadata?: Record<string, unknown>;
  description?: string;
  integration_id: string;
  product_id?: string; // ID do produto para integração workspace-scoped (legacy e v2 derivam scope 'local' + vínculo)
  scope?: 'local' | 'global'; // v2: separa visibilidade entre workspace e organização
  status?: IntegrationStatus;
  mcp_config?: Record<string, unknown>; // Override opcional do template
  // Para criação transacional com credenciais
  credentials?: Record<string, unknown>;
  settings?: SettingsInstance; // Valores simples dos campos (não a estrutura do template)
}

// Dados para atualização de integração base
export interface UpdateIntegrationBaseData {
  /** Nome do provedor, vindo do template. */
  label?: string;
  /** Nome desta instalação, dado por quem a cadastrou — é o que as telas mostram. */
  title?: string | null;
  /** Campos de `metadata` declarados pelo template — hoje, o catálogo de modelos. */
  metadata?: Record<string, unknown>;
  description?: string;
  credentials?: Record<string, unknown>; // Credenciais atualizadas
  settings?: SettingsInstance; // Valores simples dos campos (não a estrutura do template)
  mcp_config?: Record<string, unknown>; // Override opcional do template
  auth_fields?: Record<string, unknown>;
  status?: IntegrationStatus;
  scope?: 'local' | 'global';
  product_id?: string;
}

// Erro de dependências ao tentar excluir integração
export interface IntegrationDependenciesError {
  success: false;
  message: string;
  error: 'INTEGRATION_HAS_DEPENDENCIES';
  dependencies: {
    organizations: number;
    agentIntegrations: number;
    secrets: number;
  };
  details: string;
}

// Erro customizado para tratamento no frontend
export class IntegrationDependencyError extends Error {
  public readonly dependencies: {
    organizations: number;
    agentIntegrations: number;
    secrets: number;
  };
  public readonly details: string;

  constructor(errorData: IntegrationDependenciesError) {
    super(errorData.message);
    this.name = 'IntegrationDependencyError';
    this.dependencies = errorData.dependencies;
    this.details = errorData.details;
  }
}

export { AUTH_FIELD_CONFIGS } from "@/features/Auth/types/auth-fields";
